import { supabase } from '@/lib/supabase/client';
import { ROUTES } from '@/constants/routes';
import { slaEsignConfig } from '@/lib/config/sla-esign';
import { applySlaAgreementWriteback } from '@/lib/sla/writeback';
import {
  normalizeSlaAgreement,
  normalizeSlaAgreementEvent,
  normalizeSlaAgreementRevisionRequest,
  normalizeSlaAgreementSendPackage,
  normalizeSlaAgreementSigner,
  normalizeSlaAgreementSignerToken,
} from '@/lib/sla/normalize';
import { uploadSlaAgreementFinalizedFile } from '@/lib/services/storage';
import type {
  LogSlaSigningUiEventInput,
  LogSlaSigningUiEventResponse,
  OpenSlaSigningSessionResponse,
  RequestSlaSignatureRevisionResponse,
  SlaAgreement,
  SlaAgreementCustomerSubmission,
  SlaAgreementEvent,
  SlaAgreementEventActorType,
  SlaAgreementReconciliation,
  SlaAgreementRevisionRequest,
  SlaAgreementSendPackage,
  SlaAgreementSigner,
  SlaAgreementSignerToken,
  SlaAgreementWritebackResult,
  SubmitSlaCustomerSignatureResponse,
} from '@/types/sla-agreements';

const SIGNATURE_REQUEST_EXPIRY_DAYS = 14;

interface CurrentUserContext {
  id: string | null;
  email: string | null;
  label: string | null;
}

interface TokenRpcResponse {
  token_id: string;
  raw_token: string;
  expires_at: string;
}

interface LogEventInput {
  agreementId: string;
  eventType: string;
  sendPackageId?: string | null;
  signerId?: string | null;
  tokenId?: string | null;
  packageVersion?: number | null;
  actorType?: SlaAgreementEventActorType;
  actorUserId?: string | null;
  actorLabel?: string | null;
  details?: Record<string, unknown> | null;
}

interface CreateSendPackageInput {
  agreement: SlaAgreement;
}

interface CreateSignersInput {
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
}

interface SendSignatureRequestInput {
  agreementId: string;
  resend?: boolean;
}

interface InvokeCustomerSignatureInput {
  token: string;
  submission: SlaAgreementCustomerSubmission;
}

interface InvokeRevisionRequestInput {
  token: string;
  message: string;
}

interface TpbCountersignSession {
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
  tpbSigner: SlaAgreementSigner;
  customerSigner: SlaAgreementSigner;
  openRevisionRequest: SlaAgreementRevisionRequest | null;
}

interface FinalizedArtifact {
  blob: Blob;
  file: File;
  filename: string;
}

interface FinalizedArtifactContext {
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
  reconciliation: SlaAgreementReconciliation;
  customerSigner: SlaAgreementSigner;
  tpbSigner: SlaAgreementSigner;
}

interface SubmitTpbCountersignAndFinalizeInput {
  agreementId: string;
  signatureText: string;
  buildFinalizedArtifact: (context: FinalizedArtifactContext) => Promise<FinalizedArtifact>;
}

interface RetryFinalizationInput {
  agreementId: string;
  buildFinalizedArtifact?: (context: FinalizedArtifactContext) => Promise<FinalizedArtifact>;
}

interface RetryCompletedDeliveryInput {
  agreementId: string;
}

interface FinalizationResult {
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
  writebackResult: SlaAgreementWritebackResult;
  deliveryEmailId: string | null;
  finalizedFilePath: string;
}

function getFutureSigningUrl(rawToken: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';
  return `${origin}${ROUTES.SLA_AGREEMENT_SIGN(rawToken)}`;
}

function buildSignatureRequestSubject(agreement: SlaAgreement): string {
  return `Please sign ${agreement.agreement_number}`;
}

function buildSignatureRequestText(input: {
  agreement: SlaAgreement;
  customerName: string | null;
  signingUrl: string;
}): string {
  const customerName = input.customerName?.trim() || 'there';
  return [
    `Hi ${customerName},`,
    '',
    `Please review and sign license agreement ${input.agreement.agreement_number} using the secure link below:`,
    input.signingUrl,
    '',
    `If you have questions, reply to this email and the TPB team will follow up.`,
    '',
    'Thanks,',
    'The Peak Beyond',
  ].join('\n');
}

function buildFinalizedDeliverySubject(agreement: SlaAgreement): string {
  return `Finalized ${agreement.agreement_number}`;
}

function buildFinalizedDeliveryText(input: {
  agreement: SlaAgreement;
  customerName: string | null;
}): string {
  const customerName = input.customerName?.trim() || 'there';
  return [
    `Hi ${customerName},`,
    '',
    `Attached is the finalized copy of license agreement ${input.agreement.agreement_number}.`,
    '',
    'Thanks,',
    'The Peak Beyond',
  ].join('\n');
}

function normalizeComparableText(value: string | null | undefined): string {
  return (value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function normalizeOptionalText(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

function isEmailLike(value: string | null | undefined): boolean {
  const normalizedValue = normalizeOptionalText(value);
  return normalizedValue ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue) : false;
}

function getPreferredHumanReadableSignerName(input: {
  existingName?: string | null;
  actorLabel?: string | null;
  typedSignature?: string | null;
}) {
  const existingName = normalizeOptionalText(input.existingName);
  if (existingName && !isEmailLike(existingName)) {
    return existingName;
  }

  const actorLabel = normalizeOptionalText(input.actorLabel);
  if (actorLabel && !isEmailLike(actorLabel)) {
    return actorLabel;
  }

  const typedSignature = normalizeOptionalText(input.typedSignature);
  if (typedSignature && !isEmailLike(typedSignature)) {
    return typedSignature;
  }

  return null;
}

function ensureAgreementReconciliation(
  agreement: SlaAgreement
): SlaAgreementReconciliation {
  if (!agreement.reconciliation_json) {
    throw new Error('Customer reconciliation data is required before TPB countersign.');
  }

  return agreement.reconciliation_json;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve((dataUrl?.split(',')[1]) ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function getCurrentUserContext(): Promise<CurrentUserContext> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const label =
    typeof user?.user_metadata?.full_name === 'string'
      ? user.user_metadata.full_name
      : typeof user?.user_metadata?.name === 'string'
        ? user.user_metadata.name
        : user?.email ?? null;

  return {
    id: user?.id ?? null,
    email: user?.email ?? null,
    label,
  };
}

async function fetchAgreementById(id: string): Promise<SlaAgreement | null> {
  const { data, error } = await supabase.from('sla_agreements').select('*').eq('id', id).single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching SLA agreement for signing:', error);
    throw new Error(`Failed to fetch SLA agreement: ${error.message}`);
  }

  return normalizeSlaAgreement(data as Record<string, unknown>);
}

async function updateAgreement(id: string, input: Record<string, unknown>): Promise<SlaAgreement> {
  const currentUser = await getCurrentUserContext();
  const { data, error } = await supabase
    .from('sla_agreements')
    .update({
      ...input,
      updated_by: currentUser.id,
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating SLA agreement signing state:', error);
    throw new Error(`Failed to update SLA agreement signing state: ${error.message}`);
  }

  return normalizeSlaAgreement(data as Record<string, unknown>);
}

async function fetchSendPackageById(id: string): Promise<SlaAgreementSendPackage | null> {
  const { data, error } = await supabase
    .from('sla_agreement_send_packages')
    .select(`
      *,
      signers:sla_agreement_signers (
        *,
        tokens:sla_agreement_signer_tokens (*)
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching SLA send package:', error);
    throw new Error(`Failed to fetch SLA send package: ${error.message}`);
  }

  return normalizeSlaAgreementSendPackage(data as Record<string, unknown>);
}

async function getNextPackageVersion(agreementId: string): Promise<number> {
  const { data, error } = await supabase
    .from('sla_agreement_send_packages')
    .select('package_version')
    .eq('agreement_id', agreementId)
    .order('package_version', { ascending: false })
    .limit(1);

  if (error) {
    console.error('Error loading existing SLA send package versions:', error);
    throw new Error(`Failed to determine next send package version: ${error.message}`);
  }

  const currentMax = Number((data as Array<{ package_version?: number }> | null)?.[0]?.package_version || 0);
  return currentMax + 1;
}

async function updateSendPackage(
  id: string,
  input: Record<string, unknown>
): Promise<SlaAgreementSendPackage> {
  const currentUser = await getCurrentUserContext();
  const { data, error } = await supabase
    .from('sla_agreement_send_packages')
    .update({
      ...input,
      updated_by: currentUser.id,
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating SLA send package:', error);
    throw new Error(`Failed to update send package: ${error.message}`);
  }

  const normalized = normalizeSlaAgreementSendPackage(data as Record<string, unknown>);
  const fullPackage = await fetchSendPackageById(normalized.id);
  return fullPackage || normalized;
}

async function updateSignerStatuses(
  sendPackageId: string,
  input: Record<string, unknown>
): Promise<void> {
  const currentUser = await getCurrentUserContext();
  const { error } = await supabase
    .from('sla_agreement_signers')
    .update({
      ...input,
      updated_by: currentUser.id,
    })
    .eq('send_package_id', sendPackageId);

  if (error) {
    console.error('Error updating SLA send package signers:', error);
    throw new Error(`Failed to update SLA signers: ${error.message}`);
  }
}

async function updateTokenStatuses(sendPackageId: string, input: Record<string, unknown>): Promise<void> {
  const currentUser = await getCurrentUserContext();
  const { error } = await supabase
    .from('sla_agreement_signer_tokens')
    .update({
      ...input,
      updated_by: currentUser.id,
    })
    .eq('send_package_id', sendPackageId);

  if (error) {
    console.error('Error updating SLA signer tokens:', error);
    throw new Error(`Failed to update SLA signer tokens: ${error.message}`);
  }
}

async function updateRevisionRequestStatuses(sendPackageId: string, input: Record<string, unknown>): Promise<void> {
  const currentUser = await getCurrentUserContext();
  const { error } = await supabase
    .from('sla_agreement_revision_requests')
    .update({
      ...input,
      resolved_by: input.resolved_by ?? currentUser.id,
    })
    .eq('send_package_id', sendPackageId)
    .eq('status', 'open');

  if (error) {
    console.error('Error updating SLA revision requests:', error);
    throw new Error(`Failed to update SLA revision requests: ${error.message}`);
  }
}

async function supersedePackageTree(
  agreementId: string,
  sendPackage: SlaAgreementSendPackage,
  actor: CurrentUserContext
): Promise<void> {
  const now = new Date().toISOString();
  await updateSendPackage(sendPackage.id, {
    status: 'superseded',
    superseded_at: now,
  });
  await updateSignerStatuses(sendPackage.id, {
    status: 'superseded',
    superseded_at: now,
  });
  await updateTokenStatuses(sendPackage.id, {
    status: 'superseded',
    superseded_at: now,
  });
  await updateRevisionRequestStatuses(sendPackage.id, {
    status: 'superseded',
    resolved_at: now,
    resolved_by: actor.id,
  });
  await slaAgreementSigningService.logEvent({
    agreementId,
    eventType: 'send_package_superseded',
    sendPackageId: sendPackage.id,
    packageVersion: sendPackage.package_version,
    actorType: 'user',
    actorUserId: actor.id,
    actorLabel: actor.label,
  });
}

function isPackagePastExpiry(sendPackage: SlaAgreementSendPackage | null): boolean {
  if (!sendPackage?.expires_at || sendPackage.status !== 'sent') {
    return false;
  }

  return new Date(sendPackage.expires_at).getTime() <= Date.now();
}

function getCustomerSigner(signers: SlaAgreementSigner[]): SlaAgreementSigner | null {
  return signers.find((signer) => signer.role === 'customer') || null;
}

function getTpbSigner(signers: SlaAgreementSigner[]): SlaAgreementSigner | null {
  return signers.find((signer) => signer.role === 'tpb') || null;
}

async function fetchOpenRevisionRequest(
  sendPackageId: string
): Promise<SlaAgreementRevisionRequest | null> {
  const { data, error } = await supabase
    .from('sla_agreement_revision_requests')
    .select('*')
    .eq('send_package_id', sendPackageId)
    .eq('status', 'open')
    .maybeSingle();

  if (error) {
    console.error('Error fetching open SLA revision request:', error);
    throw new Error(`Failed to fetch open revision request: ${error.message}`);
  }

  return data ? normalizeSlaAgreementRevisionRequest(data as Record<string, unknown>) : null;
}

async function hasMatchingEvent(input: {
  agreementId: string;
  sendPackageId: string;
  eventType: string;
  actorUserId?: string | null;
}): Promise<boolean> {
  let query = supabase
    .from('sla_agreement_events')
    .select('id')
    .eq('agreement_id', input.agreementId)
    .eq('send_package_id', input.sendPackageId)
    .eq('event_type', input.eventType)
    .limit(1);

  if (input.actorUserId) {
    query = query.eq('actor_user_id', input.actorUserId);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error checking SLA agreement events:', error);
    throw new Error(`Failed to check agreement events: ${error.message}`);
  }

  return Boolean((data as Array<{ id?: string }> | null)?.length);
}

async function updateSignerById(id: string, input: Record<string, unknown>): Promise<SlaAgreementSigner> {
  const currentUser = await getCurrentUserContext();
  const { data, error } = await supabase
    .from('sla_agreement_signers')
    .update({
      ...input,
      updated_by: currentUser.id,
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating SLA signer:', error);
    throw new Error(`Failed to update signer: ${error.message}`);
  }

  return normalizeSlaAgreementSigner(data as Record<string, unknown>);
}

async function downloadContractBlob(path: string): Promise<Blob> {
  const { data, error } = await supabase.storage.from('contracts').download(path);

  if (error || !data) {
    console.error('Error downloading finalized SLA agreement artifact:', error);
    throw new Error(`Failed to load finalized agreement artifact: ${error?.message || 'Missing artifact.'}`);
  }

  return data;
}

async function sendFinalizedDeliveryEmail(input: {
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
  customerSigner: SlaAgreementSigner;
  blob: Blob;
  filename: string;
  actor: CurrentUserContext;
}): Promise<string | null> {
  const pdfBase64 = await blobToBase64(input.blob);
  const subject = buildFinalizedDeliverySubject(input.agreement);
  const text = buildFinalizedDeliveryText({
    agreement: input.agreement,
    customerName: input.customerSigner.name || input.agreement.draft_payload_json.customer_name,
  });

  const { data, error } = await supabase.functions.invoke('send-sla-finalized-delivery', {
    body: {
      agreementId: input.agreement.id,
      sendPackageId: input.sendPackage.id,
      to: input.customerSigner.email,
      subject,
      text,
      pdfBase64,
      filename: input.filename,
      fromEmail: input.actor.email ?? undefined,
    },
  });

  if (error || data?.error || !data?.success) {
    const failureDetails =
      typeof data?.details === 'string'
        ? data.details
        : error?.message || data?.error || 'Failed to send finalized agreement email.';
    throw new Error(failureDetails);
  }

  return typeof data?.emailId === 'string' ? data.emailId : null;
}

export const slaAgreementSigningService = {
  async createSendPackage(input: CreateSendPackageInput): Promise<SlaAgreementSendPackage> {
    const currentUser = await getCurrentUserContext();
    const packageVersion = await getNextPackageVersion(input.agreement.id);

    const { data, error } = await supabase
      .from('sla_agreement_send_packages')
      .insert({
        agreement_id: input.agreement.id,
        package_version: packageVersion,
        status: 'created',
        agreement_number: input.agreement.agreement_number,
        template_key: input.agreement.template_key,
        template_version: input.agreement.template_version,
        generated_file_path: input.agreement.generated_file_path,
        draft_payload_json: input.agreement.draft_payload_json,
        created_by: currentUser.id,
        updated_by: currentUser.id,
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error creating SLA send package:', error);
      throw new Error(`Failed to create send package: ${error.message}`);
    }

    return normalizeSlaAgreementSendPackage(data as Record<string, unknown>);
  },

  async createSigners(input: CreateSignersInput): Promise<SlaAgreementSigner[]> {
    const currentUser = await getCurrentUserContext();
    const customerEmail = input.agreement.draft_payload_json.signer_email?.trim() || null;
    const customerName = input.agreement.draft_payload_json.signer_name?.trim() || null;
    const customerTitle = input.agreement.draft_payload_json.signer_title?.trim() || null;

    const { data, error } = await supabase
      .from('sla_agreement_signers')
      .insert([
        {
          agreement_id: input.agreement.id,
          send_package_id: input.sendPackage.id,
          role: 'customer',
          status: 'pending',
          name: customerName,
          title: customerTitle,
          email: customerEmail,
          created_by: currentUser.id,
          updated_by: currentUser.id,
        },
        {
          agreement_id: input.agreement.id,
          send_package_id: input.sendPackage.id,
          role: 'tpb',
          status: 'pending',
          internal_user_id: currentUser.id,
          name: currentUser.label,
          title: null,
          email: currentUser.email,
          created_by: currentUser.id,
          updated_by: currentUser.id,
        },
      ])
      .select('*');

    if (error) {
      console.error('Error creating SLA signers:', error);
      throw new Error(`Failed to create signers: ${error.message}`);
    }

    return ((data as Record<string, unknown>[]) || []).map((row) => normalizeSlaAgreementSigner(row));
  },

  async issueCustomerToken(input: {
    agreementId: string;
    sendPackageId: string;
    signerId: string;
    expiresAt?: string;
  }): Promise<{ token: SlaAgreementSignerToken; rawToken: string }> {
    const expiresAt =
      input.expiresAt || new Date(Date.now() + SIGNATURE_REQUEST_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase.rpc('issue_sla_agreement_signer_token', {
      p_send_package_id: input.sendPackageId,
      p_signer_id: input.signerId,
      p_expires_at: expiresAt,
    });

    if (error) {
      console.error('Error issuing SLA customer token:', error);
      throw new Error(`Failed to issue customer token: ${error.message}`);
    }

    const tokenResponse = Array.isArray(data)
      ? (data[0] as TokenRpcResponse | undefined)
      : ((data as TokenRpcResponse | null) ?? undefined);

    if (!tokenResponse?.token_id || !tokenResponse.raw_token || !tokenResponse.expires_at) {
      throw new Error('Failed to issue customer token.');
    }

    const { data: tokenRow, error: tokenError } = await supabase
      .from('sla_agreement_signer_tokens')
      .select('*')
      .eq('id', tokenResponse.token_id)
      .single();

    if (tokenError) {
      console.error('Error loading issued SLA token:', tokenError);
      throw new Error(`Failed to load issued customer token: ${tokenError.message}`);
    }

    return {
      token: normalizeSlaAgreementSignerToken(tokenRow as Record<string, unknown>),
      rawToken: tokenResponse.raw_token,
    };
  },

  async logEvent(input: LogEventInput): Promise<SlaAgreementEvent> {
    const { data, error } = await supabase
      .from('sla_agreement_events')
      .insert({
        agreement_id: input.agreementId,
        send_package_id: input.sendPackageId ?? null,
        signer_id: input.signerId ?? null,
        token_id: input.tokenId ?? null,
        package_version: input.packageVersion ?? null,
        actor_type: input.actorType ?? 'system',
        actor_user_id: input.actorUserId ?? null,
        actor_label: input.actorLabel ?? null,
        event_type: input.eventType,
        details_json: input.details ?? {},
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error logging SLA agreement event:', error);
      throw new Error(`Failed to log agreement event: ${error.message}`);
    }

    return normalizeSlaAgreementEvent(data as Record<string, unknown>);
  },

  async getCurrentSendPackage(agreementId: string): Promise<SlaAgreementSendPackage | null> {
    const agreement = await fetchAgreementById(agreementId);
    if (!agreement?.current_send_package_id) {
      return null;
    }

    let sendPackage = await fetchSendPackageById(agreement.current_send_package_id);
    if (isPackagePastExpiry(sendPackage)) {
      await this.expireSendPackage(agreementId);
      sendPackage = await fetchSendPackageById(agreement.current_send_package_id);
    }

    return sendPackage;
  },

  async getTimeline(agreementId: string): Promise<SlaAgreementEvent[]> {
    const { data, error } = await supabase
      .from('sla_agreement_events')
      .select('*')
      .eq('agreement_id', agreementId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching SLA agreement timeline:', error);
      throw new Error(`Failed to fetch agreement timeline: ${error.message}`);
    }

    return ((data as Record<string, unknown>[]) || []).map((row) => normalizeSlaAgreementEvent(row));
  },

  async revokeActiveSendPackage(agreementId: string): Promise<SlaAgreement> {
    const agreement = await fetchAgreementById(agreementId);
    if (!agreement?.current_send_package_id) {
      if (!agreement) {
        throw new Error('Agreement not found.');
      }
      return agreement;
    }

    const sendPackage = await fetchSendPackageById(agreement.current_send_package_id);
    if (!sendPackage) {
      return updateAgreement(agreement.id, {
        status: 'generated',
        current_send_package_id: null,
        signature_request_revoked_at: new Date().toISOString(),
        signature_request_expires_at: null,
        signature_request_email_id: null,
      });
    }

    const actor = await getCurrentUserContext();
    const now = new Date().toISOString();
    await updateSendPackage(sendPackage.id, {
      status: 'revoked',
      revoked_at: now,
    });
    await updateSignerStatuses(sendPackage.id, {
      status: 'revoked',
      revoked_at: now,
    });
    await updateTokenStatuses(sendPackage.id, {
      status: 'revoked',
      revoked_at: now,
    });
    await updateRevisionRequestStatuses(sendPackage.id, {
      status: 'superseded',
      resolved_at: now,
      resolved_by: actor.id,
    });
    await this.logEvent({
      agreementId,
      eventType: 'send_package_revoked',
      sendPackageId: sendPackage.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorUserId: actor.id,
      actorLabel: actor.label,
    });

    return updateAgreement(agreementId, {
      status: 'generated',
      current_send_package_id: null,
      signature_request_revoked_at: now,
      signature_request_expires_at: null,
      signature_request_email_id: null,
    });
  },

  async expireSendPackage(agreementId: string): Promise<SlaAgreement> {
    const agreement = await fetchAgreementById(agreementId);
    if (!agreement?.current_send_package_id) {
      if (!agreement) {
        throw new Error('Agreement not found.');
      }
      return agreement;
    }

    const sendPackage = await fetchSendPackageById(agreement.current_send_package_id);
    if (!sendPackage || sendPackage.status === 'expired') {
      return agreement;
    }

    const now = new Date().toISOString();
    await updateSendPackage(sendPackage.id, {
      status: 'expired',
      expired_at: now,
    });
    await updateSignerStatuses(sendPackage.id, {
      status: 'expired',
      expired_at: now,
    });
    await updateTokenStatuses(sendPackage.id, {
      status: 'expired',
      expired_at: now,
    });
    await this.logEvent({
      agreementId,
      eventType: 'send_package_expired',
      sendPackageId: sendPackage.id,
      packageVersion: sendPackage.package_version,
      actorType: 'system',
    });

    return updateAgreement(agreementId, {
      status: 'expired',
      signature_request_expires_at: sendPackage.expires_at || now,
    });
  },

  async sendSignatureRequest(input: SendSignatureRequestInput): Promise<{
    agreement: SlaAgreement;
    sendPackage: SlaAgreementSendPackage;
  }> {
    if (!slaEsignConfig.publicSigningEnabled) {
      throw new Error('Electronic signing is disabled for this environment.');
    }

    const agreement = await fetchAgreementById(input.agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    if (!agreement.generated_file_path) {
      throw new Error('Generate the agreement PDF before sending for signature.');
    }

    if (
      ![
        'generated',
        'signature_requested',
        'customer_viewed',
        'customer_in_progress',
        'revision_requested',
        'expired',
      ].includes(agreement.status)
    ) {
      throw new Error('This agreement is not in a sendable state for signature requests.');
    }

    const customerEmail = agreement.draft_payload_json.signer_email?.trim();
    if (!customerEmail) {
      throw new Error('Customer signer email is required before sending for signature.');
    }

    const actor = await getCurrentUserContext();
    const previousPackage =
      agreement.current_send_package_id && input.resend
        ? await fetchSendPackageById(agreement.current_send_package_id)
        : null;

    const sendPackage = await this.createSendPackage({ agreement });
    await this.logEvent({
      agreementId: agreement.id,
      eventType: 'send_package_created',
      sendPackageId: sendPackage.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorUserId: actor.id,
      actorLabel: actor.label,
    });

    const signers = await this.createSigners({ agreement, sendPackage });
    await this.logEvent({
      agreementId: agreement.id,
      eventType: 'signers_created',
      sendPackageId: sendPackage.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorUserId: actor.id,
      actorLabel: actor.label,
      details: {
        signerRoles: signers.map((signer) => signer.role),
      },
    });

    const customerSigner = getCustomerSigner(signers);
    if (!customerSigner) {
      throw new Error('Customer signer could not be created.');
    }

    const issuedToken = await this.issueCustomerToken({
      agreementId: agreement.id,
      sendPackageId: sendPackage.id,
      signerId: customerSigner.id,
    });
    await this.logEvent({
      agreementId: agreement.id,
      eventType: 'customer_token_issued',
      sendPackageId: sendPackage.id,
      signerId: customerSigner.id,
      tokenId: issuedToken.token.id,
      packageVersion: sendPackage.package_version,
      actorType: 'system',
      details: {
        expiresAt: issuedToken.token.expires_at,
      },
    });

    const signingUrl = getFutureSigningUrl(issuedToken.rawToken);
    const subject = buildSignatureRequestSubject(agreement);
    const text = buildSignatureRequestText({
      agreement,
      customerName: agreement.draft_payload_json.signer_name || agreement.draft_payload_json.customer_name,
      signingUrl,
    });

    const { data, error } = await supabase.functions.invoke('send-sla-signature-request', {
      body: {
        agreementId: agreement.id,
        sendPackageId: sendPackage.id,
        signerId: customerSigner.id,
        to: customerEmail,
        subject,
        text,
        signingUrl,
        fromEmail: actor.email ?? undefined,
      },
    });

    if (error || data?.error || !data?.success) {
      const failureDetails =
        typeof data?.details === 'string'
          ? data.details
          : error?.message || data?.error || 'Failed to send signature request email.';

      const now = new Date().toISOString();
      await updateSendPackage(sendPackage.id, {
        status: 'send_failed',
      });
      await supabase
        .from('sla_agreement_signer_tokens')
        .update({
          status: 'revoked',
          revoked_at: now,
          updated_by: actor.id,
        })
        .eq('id', issuedToken.token.id);
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'signature_request_send_failed',
        sendPackageId: sendPackage.id,
        signerId: customerSigner.id,
        tokenId: issuedToken.token.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          error: failureDetails,
        },
      });

      throw new Error(failureDetails);
    }

    const now = new Date().toISOString();
    await updateSendPackage(sendPackage.id, {
      status: 'sent',
      sent_at: now,
      expires_at: issuedToken.token.expires_at,
      email_id: data.emailId ?? null,
    });
    const { error: signerUpdateError } = await supabase
      .from('sla_agreement_signers')
      .update({
        status: 'emailed',
        emailed_at: now,
        updated_by: actor.id,
      })
      .eq('id', customerSigner.id);

    if (signerUpdateError) {
      console.error('Error updating SLA customer signer status:', signerUpdateError);
      throw new Error(`Failed to update customer signer status: ${signerUpdateError.message}`);
    }

    if (previousPackage) {
      await supersedePackageTree(agreement.id, previousPackage, actor);
    }

    const updatedAgreement = await updateAgreement(agreement.id, {
      status: 'signature_requested',
      current_send_package_id: sendPackage.id,
      signature_requested_at: now,
      signature_request_expires_at: issuedToken.token.expires_at,
      signature_request_revoked_at: null,
      signature_request_email_id: data.emailId ?? null,
    });
    await this.logEvent({
      agreementId: agreement.id,
      eventType: input.resend ? 'signature_request_resent' : 'signature_request_sent',
      sendPackageId: sendPackage.id,
      signerId: customerSigner.id,
      tokenId: issuedToken.token.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorUserId: actor.id,
      actorLabel: actor.label,
      details: {
        to: customerEmail,
        emailId: data.emailId ?? null,
        expiresAt: issuedToken.token.expires_at,
      },
    });

    const refreshedPackage = await fetchSendPackageById(sendPackage.id);
    return {
      agreement: updatedAgreement,
      sendPackage: refreshedPackage || sendPackage,
    };
  },

  async getOpenRevisionRequest(sendPackageId: string): Promise<SlaAgreementRevisionRequest | null> {
    return fetchOpenRevisionRequest(sendPackageId);
  },

  async openTpbCountersignSession(agreementId: string): Promise<TpbCountersignSession> {
    let agreement = await fetchAgreementById(agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    if (!agreement.current_send_package_id) {
      throw new Error('This agreement does not have an active e-sign package.');
    }

    let sendPackage = await fetchSendPackageById(agreement.current_send_package_id);
    if (!sendPackage) {
      throw new Error('Active send package not found.');
    }

    if (isPackagePastExpiry(sendPackage)) {
      await this.expireSendPackage(agreementId);
      throw new Error('This signature request has expired.');
    }

    if (agreement.status === 'customer_signed') {
      agreement = await updateAgreement(agreement.id, {
        status: 'awaiting_tpb_signature',
      });
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_awaiting_tpb_signature',
        sendPackageId: sendPackage.id,
        packageVersion: sendPackage.package_version,
        actorType: 'system',
      });
    }

    if (agreement.status !== 'awaiting_tpb_signature') {
      throw new Error('This agreement is not ready for TPB countersign.');
    }

    if (sendPackage.status !== 'sent') {
      throw new Error('This send package is not active for countersign.');
    }

    const actor = await getCurrentUserContext();
    if (!actor.id) {
      throw new Error('Authenticated TPB user required.');
    }

    let customerSigner = getCustomerSigner(sendPackage.signers);
    let tpbSigner = getTpbSigner(sendPackage.signers);

    if (!customerSigner || !tpbSigner) {
      throw new Error('The active send package is missing required signer records.');
    }

    const openRevisionRequest = await fetchOpenRevisionRequest(sendPackage.id);
    if (openRevisionRequest || customerSigner.status === 'revision_requested') {
      throw new Error('Resolve the customer revision request before countersigning.');
    }

    if (customerSigner.status !== 'signed') {
      throw new Error('Customer signature must be completed before TPB countersign.');
    }

    if (tpbSigner.internal_user_id && tpbSigner.internal_user_id !== actor.id) {
      throw new Error('This agreement is assigned to another TPB signer.');
    }

    if (!tpbSigner.internal_user_id) {
      const preferredSignerName = getPreferredHumanReadableSignerName({
        existingName: tpbSigner.name,
        actorLabel: actor.label,
      });

      await updateSignerById(tpbSigner.id, {
        internal_user_id: actor.id,
        name: preferredSignerName,
        email: actor.email ?? tpbSigner.email,
      });

      sendPackage = (await fetchSendPackageById(sendPackage.id)) || sendPackage;
      customerSigner = getCustomerSigner(sendPackage.signers);
      tpbSigner = getTpbSigner(sendPackage.signers);

      if (!customerSigner || !tpbSigner) {
        throw new Error('Failed to refresh signer assignment.');
      }
    }

    const alreadyOpened = await hasMatchingEvent({
      agreementId: agreement.id,
      sendPackageId: sendPackage.id,
      eventType: 'agreement_tpb_countersign_opened',
      actorUserId: actor.id,
    });

    if (!alreadyOpened) {
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_tpb_countersign_opened',
        sendPackageId: sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
      });
    }

    return {
      agreement,
      sendPackage,
      tpbSigner,
      customerSigner,
      openRevisionRequest,
    };
  },

  async submitTpbCountersignAndFinalize(
    input: SubmitTpbCountersignAndFinalizeInput
  ): Promise<FinalizationResult> {
    const actor = await getCurrentUserContext();
    if (!actor.id) {
      throw new Error('Authenticated TPB user required.');
    }

    const session = await this.openTpbCountersignSession(input.agreementId);
    const normalizedSignatureText = normalizeOptionalText(input.signatureText);
    const comparableSignerName = normalizeComparableText(
      getPreferredHumanReadableSignerName({
        existingName: session.tpbSigner.name,
        actorLabel: actor.label,
        typedSignature: normalizedSignatureText,
      })
    );

    if (!normalizedSignatureText) {
      throw new Error('Typed TPB signature is required.');
    }

    if (comparableSignerName && normalizeComparableText(normalizedSignatureText) !== comparableSignerName) {
      throw new Error('Typed TPB signature must match the assigned TPB signer name.');
    }

    let tpbSigner = session.tpbSigner;
    const now = new Date().toISOString();
    if (!tpbSigner.signed_at || !tpbSigner.signature_text) {
      const preferredSignerName = getPreferredHumanReadableSignerName({
        existingName: tpbSigner.name,
        actorLabel: actor.label,
        typedSignature: normalizedSignatureText,
      });

      tpbSigner = await updateSignerById(tpbSigner.id, {
        status: 'signed',
        name: preferredSignerName,
        signed_at: now,
        signature_type: 'typed',
        signature_text: normalizedSignatureText,
        last_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_tpb_signature_submitted',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
      });
    } else if (
      normalizeComparableText(tpbSigner.signature_text) !== normalizeComparableText(normalizedSignatureText)
    ) {
      throw new Error('TPB signature is already recorded with a different signer value.');
    }

    const reconciliation = ensureAgreementReconciliation(session.agreement);

    try {
      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_finalization_started',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
      });

      const artifact = await input.buildFinalizedArtifact({
        agreement: session.agreement,
        sendPackage: session.sendPackage,
        reconciliation,
        customerSigner: session.customerSigner,
        tpbSigner,
      });

      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_final_pdf_generated',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          filename: artifact.filename,
        },
      });

      const upload = await uploadSlaAgreementFinalizedFile(session.agreement.id, artifact.file);
      const finalizedAt = new Date().toISOString();

      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_final_pdf_stored',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          finalizedFilePath: upload.path,
        },
      });

      const agreementWithArtifact = await updateAgreement(session.agreement.id, {
        finalized_file_path: upload.path,
        signed_file_path: upload.path,
        finalized_at: finalizedAt,
      });

      let writebackResult: SlaAgreementWritebackResult;
      try {
        writebackResult = await applySlaAgreementWriteback({
          agreement: {
            ...agreementWithArtifact,
            draft_payload_json: session.sendPackage.draft_payload_json,
            reconciliation_json: reconciliation,
          },
          reconciliation,
          signedContractPath: upload.path,
        });
      } catch (error) {
        await this.logEvent({
          agreementId: session.agreement.id,
          eventType: 'agreement_writeback_failed',
          sendPackageId: session.sendPackage.id,
          signerId: tpbSigner.id,
          packageVersion: session.sendPackage.package_version,
          actorType: 'user',
          actorUserId: actor.id,
          actorLabel: actor.label,
          details: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
        throw error;
      }

      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_writeback_succeeded',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          finalizedFilePath: upload.path,
        },
      });

      let completedAgreement = await updateAgreement(session.agreement.id, {
        status: 'completed',
        finalized_file_path: upload.path,
        signed_file_path: upload.path,
        finalized_at: finalizedAt,
        writeback_result_json: writebackResult,
        completed_at: finalizedAt,
      });

      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_completed',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
      });

      let deliveryEmailId: string | null = null;
      if (session.customerSigner.email) {
        try {
          deliveryEmailId = await sendFinalizedDeliveryEmail({
            agreement: completedAgreement,
            sendPackage: session.sendPackage,
            customerSigner: session.customerSigner,
            blob: artifact.blob,
            filename: artifact.filename,
            actor,
          });

          const deliveredAt = new Date().toISOString();
          completedAgreement = await updateAgreement(session.agreement.id, {
            completed_delivery_at: deliveredAt,
            completed_delivery_email_id: deliveryEmailId,
          });
          await this.logEvent({
            agreementId: session.agreement.id,
            eventType: 'agreement_final_delivery_sent',
            sendPackageId: session.sendPackage.id,
            signerId: session.customerSigner.id,
            packageVersion: session.sendPackage.package_version,
            actorType: 'user',
            actorUserId: actor.id,
            actorLabel: actor.label,
            details: {
              emailId: deliveryEmailId,
              to: session.customerSigner.email,
            },
          });
        } catch (error) {
          await this.logEvent({
            agreementId: session.agreement.id,
            eventType: 'agreement_final_delivery_failed',
            sendPackageId: session.sendPackage.id,
            signerId: session.customerSigner.id,
            packageVersion: session.sendPackage.package_version,
            actorType: 'user',
            actorUserId: actor.id,
            actorLabel: actor.label,
            details: {
              error: error instanceof Error ? error.message : String(error),
            },
          });
        }
      } else {
        await this.logEvent({
          agreementId: session.agreement.id,
          eventType: 'agreement_final_delivery_failed',
          sendPackageId: session.sendPackage.id,
          signerId: session.customerSigner.id,
          packageVersion: session.sendPackage.package_version,
          actorType: 'user',
          actorUserId: actor.id,
          actorLabel: actor.label,
          details: {
            error: 'Customer signer email is missing.',
          },
        });
      }

      return {
        agreement: completedAgreement,
        sendPackage: (await fetchSendPackageById(session.sendPackage.id)) || session.sendPackage,
        writebackResult,
        deliveryEmailId,
        finalizedFilePath: upload.path,
      };
    } catch (error) {
      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_finalization_failed',
        sendPackageId: session.sendPackage.id,
        signerId: tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      throw error;
    }
  },

  async retryFinalization(input: RetryFinalizationInput): Promise<FinalizationResult> {
    const actor = await getCurrentUserContext();
    if (!actor.id) {
      throw new Error('Authenticated TPB user required.');
    }

    const session = await this.openTpbCountersignSession(input.agreementId);
    const reconciliation = ensureAgreementReconciliation(session.agreement);

    if (!session.tpbSigner.signed_at || !session.tpbSigner.signature_text) {
      throw new Error('TPB signature must be recorded before retrying finalization.');
    }

    let finalizedFilePath = session.agreement.finalized_file_path || session.agreement.signed_file_path;
    let artifactBlob: Blob | null = null;
    let artifactFilename = `${session.agreement.agreement_number}-finalized.pdf`;

    await this.logEvent({
      agreementId: session.agreement.id,
      eventType: 'agreement_finalization_started',
      sendPackageId: session.sendPackage.id,
      signerId: session.tpbSigner.id,
      packageVersion: session.sendPackage.package_version,
      actorType: 'user',
      actorUserId: actor.id,
      actorLabel: actor.label,
      details: {
        retry: true,
      },
    });

    try {
      if (!finalizedFilePath) {
        if (!input.buildFinalizedArtifact) {
          throw new Error('A finalized artifact builder is required when no finalized file exists.');
        }

        const artifact = await input.buildFinalizedArtifact({
          agreement: session.agreement,
          sendPackage: session.sendPackage,
          reconciliation,
          customerSigner: session.customerSigner,
          tpbSigner: session.tpbSigner,
        });
        const upload = await uploadSlaAgreementFinalizedFile(session.agreement.id, artifact.file);
        artifactBlob = artifact.blob;
        artifactFilename = artifact.filename;
        finalizedFilePath = upload.path;

        await updateAgreement(session.agreement.id, {
          finalized_file_path: upload.path,
          signed_file_path: upload.path,
          finalized_at: new Date().toISOString(),
        });

        await this.logEvent({
          agreementId: session.agreement.id,
          eventType: 'agreement_final_pdf_stored',
          sendPackageId: session.sendPackage.id,
          signerId: session.tpbSigner.id,
          packageVersion: session.sendPackage.package_version,
          actorType: 'user',
          actorUserId: actor.id,
          actorLabel: actor.label,
          details: {
            finalizedFilePath: upload.path,
            retry: true,
          },
        });
      }

      const writebackResult = await applySlaAgreementWriteback({
        agreement: {
          ...session.agreement,
          finalized_file_path: finalizedFilePath,
          signed_file_path: finalizedFilePath,
          draft_payload_json: session.sendPackage.draft_payload_json,
          reconciliation_json: reconciliation,
        },
        reconciliation,
        signedContractPath: finalizedFilePath,
      });

      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_writeback_succeeded',
        sendPackageId: session.sendPackage.id,
        signerId: session.tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          finalizedFilePath,
          retry: true,
        },
      });

      const completedAt = new Date().toISOString();
      let completedAgreement = await updateAgreement(session.agreement.id, {
        status: 'completed',
        finalized_file_path: finalizedFilePath,
        signed_file_path: finalizedFilePath,
        finalized_at: session.agreement.finalized_at || completedAt,
        writeback_result_json: writebackResult,
        completed_at: completedAt,
      });

      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_completed',
        sendPackageId: session.sendPackage.id,
        signerId: session.tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          retry: true,
        },
      });

      let deliveryEmailId: string | null = null;
      if (session.customerSigner.email) {
        try {
          const deliveryBlob = artifactBlob || (await downloadContractBlob(finalizedFilePath));
          deliveryEmailId = await sendFinalizedDeliveryEmail({
            agreement: completedAgreement,
            sendPackage: session.sendPackage,
            customerSigner: session.customerSigner,
            blob: deliveryBlob,
            filename: artifactFilename,
            actor,
          });
          const deliveredAt = new Date().toISOString();
          completedAgreement = await updateAgreement(session.agreement.id, {
            completed_delivery_at: deliveredAt,
            completed_delivery_email_id: deliveryEmailId,
          });
          await this.logEvent({
            agreementId: session.agreement.id,
            eventType: 'agreement_final_delivery_sent',
            sendPackageId: session.sendPackage.id,
            signerId: session.customerSigner.id,
            packageVersion: session.sendPackage.package_version,
            actorType: 'user',
            actorUserId: actor.id,
            actorLabel: actor.label,
            details: {
              retry: true,
              emailId: deliveryEmailId,
              to: session.customerSigner.email,
            },
          });
        } catch (error) {
          await this.logEvent({
            agreementId: session.agreement.id,
            eventType: 'agreement_final_delivery_failed',
            sendPackageId: session.sendPackage.id,
            signerId: session.customerSigner.id,
            packageVersion: session.sendPackage.package_version,
            actorType: 'user',
            actorUserId: actor.id,
            actorLabel: actor.label,
            details: {
              retry: true,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        }
      }

      return {
        agreement: completedAgreement,
        sendPackage: (await fetchSendPackageById(session.sendPackage.id)) || session.sendPackage,
        writebackResult,
        deliveryEmailId,
        finalizedFilePath,
      };
    } catch (error) {
      await this.logEvent({
        agreementId: session.agreement.id,
        eventType: 'agreement_finalization_failed',
        sendPackageId: session.sendPackage.id,
        signerId: session.tpbSigner.id,
        packageVersion: session.sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          retry: true,
          error: error instanceof Error ? error.message : String(error),
        },
      });
      throw error;
    }
  },

  async retryCompletedDelivery(input: RetryCompletedDeliveryInput): Promise<SlaAgreement> {
    if (!slaEsignConfig.finalDeliveryRetryEnabled) {
      throw new Error('Final delivery retry is disabled for this environment.');
    }

    const actor = await getCurrentUserContext();
    if (!actor.id) {
      throw new Error('Authenticated TPB user required.');
    }

    const agreement = await fetchAgreementById(input.agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    if (agreement.status !== 'completed') {
      throw new Error('Only completed agreements can retry final delivery.');
    }

    if (!agreement.finalized_file_path && !agreement.signed_file_path) {
      throw new Error('No finalized agreement file is available to deliver.');
    }

    if (!agreement.current_send_package_id) {
      throw new Error('Completed agreement is missing its send package.');
    }

    const sendPackage = await fetchSendPackageById(agreement.current_send_package_id);
    if (!sendPackage) {
      throw new Error('Send package not found.');
    }

    const customerSigner = getCustomerSigner(sendPackage.signers);
    if (!customerSigner?.email) {
      throw new Error('Customer signer email is missing.');
    }

    const finalizedFilePath = agreement.finalized_file_path || agreement.signed_file_path;
    if (!finalizedFilePath) {
      throw new Error('No finalized agreement file is available to deliver.');
    }

    const blob = await downloadContractBlob(finalizedFilePath);
    const filename = finalizedFilePath.split('/').pop() || `${agreement.agreement_number}-finalized.pdf`;

    try {
      const emailId = await sendFinalizedDeliveryEmail({
        agreement,
        sendPackage,
        customerSigner,
        blob,
        filename,
        actor,
      });
      const deliveredAt = new Date().toISOString();
      const updatedAgreement = await updateAgreement(agreement.id, {
        completed_delivery_at: deliveredAt,
        completed_delivery_email_id: emailId,
      });
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_final_delivery_sent',
        sendPackageId: sendPackage.id,
        signerId: customerSigner.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          retry: true,
          emailId,
          to: customerSigner.email,
        },
      });
      return updatedAgreement;
    } catch (error) {
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_final_delivery_failed',
        sendPackageId: sendPackage.id,
        signerId: customerSigner.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorUserId: actor.id,
        actorLabel: actor.label,
        details: {
          retry: true,
          error: error instanceof Error ? error.message : String(error),
        },
      });
      throw error;
    }
  },

  async openSigningSession(token: string): Promise<OpenSlaSigningSessionResponse> {
    const { data, error } = await supabase.functions.invoke('open-sla-signing-session', {
      body: { token },
    });

    if (error) {
      console.error('Error opening SLA signing session:', error);
      throw new Error(error.message || 'Failed to open signing session.');
    }

    return (data as OpenSlaSigningSessionResponse | null) || { ok: false, error: 'invalid_token' };
  },

  async markSigningSessionStarted(token: string): Promise<OpenSlaSigningSessionResponse> {
    return this.openSigningSession(token);
  },

  async logSigningUiEvent(
    input: LogSlaSigningUiEventInput
  ): Promise<LogSlaSigningUiEventResponse> {
    const { data, error } = await supabase.functions.invoke('log-sla-signing-ui-event', {
      body: input,
    });

    if (error) {
      console.error('Error logging SLA signing UI event:', error);
      throw new Error(error.message || 'Failed to log signing UI event.');
    }

    return (data as LogSlaSigningUiEventResponse | null) || {
      ok: false,
      error: 'validation_error',
      message: 'Missing response from log-sla-signing-ui-event.',
    };
  },

  async submitCustomerSignature(
    input: InvokeCustomerSignatureInput
  ): Promise<SubmitSlaCustomerSignatureResponse> {
    const { data, error } = await supabase.functions.invoke('submit-sla-customer-signature', {
      body: input,
    });

    if (error) {
      console.error('Error submitting SLA customer signature:', error);
      throw new Error(error.message || 'Failed to submit customer signature.');
    }

    return (data as SubmitSlaCustomerSignatureResponse | null) || {
      ok: false,
      error: 'validation_error',
      message: 'Missing response from submit-sla-customer-signature.',
    };
  },

  async requestSignatureRevision(
    input: InvokeRevisionRequestInput
  ): Promise<RequestSlaSignatureRevisionResponse> {
    const { data, error } = await supabase.functions.invoke('request-sla-signature-revision', {
      body: input,
    });

    if (error) {
      console.error('Error requesting SLA signature revision:', error);
      throw new Error(error.message || 'Failed to request revisions.');
    }

    return (data as RequestSlaSignatureRevisionResponse | null) || {
      ok: false,
      error: 'validation_error',
      message: 'Missing response from request-sla-signature-revision.',
    };
  },
};
